import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { api } from "@/api.js"
import { Confirm } from "@/components/Confirm.js"
import { Button } from "@/components/ui/button.js"

/**
 * Revoking an override: one button, one confirm, effective on the next
 * decision. Not a delete — the row stays, with who revoked it and when — so
 * the control is the same wherever the override is shown: in its row on the
 * standing list, and in the notice on the entity screen, where the person
 * reading it is usually the one who has just found out it is wrong.
 */
export function RevokeOverride({ overrideId }: { overrideId: string }) {
  const queryClient = useQueryClient()
  const [confirming, setConfirming] = useState(false)
  const revoke = useMutation({
    mutationFn: () => api.revokeOverride(overrideId),
    onSuccess: () => {
      void invalidateOverrideViews(queryClient)
      setConfirming(false)
    },
  })

  if (confirming) {
    return (
      <Confirm
        prompt="Odwołać odstępstwo?"
        pending={revoke.isPending}
        error={revoke.error}
        onConfirm={() => revoke.mutate()}
        onCancel={() => setConfirming(false)}
      />
    )
  }
  return (
    <Button size="sm" variant="ghost" onClick={() => setConfirming(true)}>
      Odwołaj
    </Button>
  )
}

/**
 * Everything that reads an override, so a change reaches the standing list,
 * the entity screen's notice and the Podmioty figures without a reload.
 */
export function invalidateOverrideViews(queryClient: ReturnType<typeof useQueryClient>) {
  return Promise.all(
    [["overrides"], ["entity"], ["entities"], ["entitySummary"]].map((queryKey) =>
      queryClient.invalidateQueries({ queryKey }),
    ),
  )
}
